import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { BarChart3, Building2, CalendarDays, ClipboardCheck, ClipboardList, FileText, Inbox, LayoutDashboard, LogOut, Route, ScrollText, Settings, UserCog, UserRoundSearch, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Loading, Pill } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { BrandLockup } from "@/components/brand";
import { Button } from "@/components/ui/button";
import ForcePasswordChange from "./ForcePasswordChange";

const ROLE_LABELS = {
  clinical_coordinator: "Coordenação clínica",
  clinician: "Profissional clínico",
  admin: "Administração",
  auditor: "Auditoria",
  content_manager: "Gestor de conteúdo",
  commercial: "Comercial / Admissões",
};

export default function PortalLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) return <Loading label="Carregando portal…" />;
  if (!user) return <Navigate to="/portal/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;

  const can = (key) => (user.permissions || []).includes(key);

  const nav = [
    { to: "/portal", label: "Visão geral", icon: LayoutDashboard, testId: "portal-nav-visao-geral" },
    { to: "/portal/pacientes", label: "Pacientes", icon: Users, testId: "portal-nav-pacientes" },
    { to: "/portal/vinculos", label: "Vínculos", icon: ClipboardList, testId: "portal-nav-vinculos" },
    ...(can("timeline")
      ? [{ to: "/portal/linha-do-tempo", label: "Linha do Tempo", icon: Route, testId: "portal-nav-linha-do-tempo" }]
      : []),
    { to: "/portal/reunioes", label: "Reuniões Clínicas", icon: ClipboardCheck, testId: "portal-nav-reunioes" },
    { to: "/portal/grade-reunioes", label: "Grade de Reuniões", icon: CalendarDays, testId: "portal-nav-grade-reunioes" },
    { to: "/portal/conteudo", label: "Conteúdo", icon: FileText, testId: "portal-nav-conteudo" },
    { to: "/portal/estrutura", label: "Estrutura", icon: Building2, testId: "portal-nav-estrutura" },
    { to: "/portal/perguntas", label: "Perguntas", icon: Inbox, testId: "portal-nav-perguntas" },
    { to: "/portal/solicitacoes", label: "Solicitações", icon: UserRoundSearch, testId: "portal-nav-solicitacoes" },
    { to: "/portal/metricas", label: "Métricas", icon: BarChart3, testId: "portal-nav-metricas" },
    ...(can("staff_manage")
      ? [{ to: "/portal/funcionarios", label: "Funcionários", icon: UserCog, testId: "portal-nav-funcionarios" }]
      : []),
    { to: "/portal/configuracoes", label: "Configurações", icon: Settings, testId: "portal-nav-configuracoes" },
    ...(["admin", "auditor", "clinical_coordinator"].includes(user.role)
      ? [{ to: "/portal/auditoria", label: "Auditoria", icon: ScrollText, testId: "portal-nav-auditoria" }]
      : []),
  ];

  return (
    <div data-testid="portal-layout" className="min-h-screen bg-bone">
      <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-3.5">
          <Link to="/portal" className="shrink-0">
            <BrandLockup size="sm" product="Operação clínica interna" testId="portal-brand" />
          </Link>

          <div className="flex items-center gap-3">
            <Avatar name={user.name} photoUrl={user.photo_thumb_url} size={38} testId="portal-user-avatar" />
            <div className="text-right">
              <p data-testid="portal-user-name" className="text-[14.5px] font-semibold text-ink-800">
                {user.name}
              </p>
              <Pill tone="info" testId="portal-user-role">
                {ROLE_LABELS[user.role] || user.role}
              </Pill>
            </div>
            <Button
              variant="ghost"
              data-testid="portal-logout"
              onClick={logout}
              title="Sair"
              className="rounded-full text-ink-500 hover:bg-sand"
            >
              <LogOut size={17} />
            </Button>
          </div>
        </div>

        <div className="border-t border-border/70">
          <nav className="mx-auto flex max-w-7xl items-center gap-1.5 overflow-x-auto px-6 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {nav.map((item) => {
              const active =
                item.to === "/portal" ? location.pathname === "/portal" : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
                    active ? "bg-sage-600 text-white shadow-sm" : "text-ink-600 hover:bg-sand"
                  }`}
                >
                  <item.icon size={16} strokeWidth={active ? 2.2 : 1.8} /> {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-9">
        <Outlet />
      </main>
    </div>
  );
}
